import React, { useCallback, useEffect } from 'react'

import classNames from 'classnames'
import { shallow } from 'zustand/shallow'

import { useDialogStore } from '@stores/dialog.store'

import DialogButtons from './DialogButtons'

export type DialogBtn =
  | {
      text?: string
      disabled?: boolean
      action?: (e: any) => void
    }
  | boolean

export type XBtn =
  | {
      action?: (e: any) => void
    }
  | boolean

export interface DialogProps {
  name: string
  title?: string
  children?: React.ReactNode
  className?: string
  maxWidth?: number | string

  okBtn?: DialogBtn
  cancelBtn?: DialogBtn

  /** Make backdrop that doesn't close dialog when clicked */
  staticBackdrop?: boolean

  /** Close the modal when escape key is pressed */
  keyboard?: boolean

  /** Function called when dialog is opened */
  onOpen?: () => void

  /** Function called when dialog is closed (x button, cancel, backdrop)*/
  onClose?: () => void

  /** Whether the action being performed is destructive (e.g. delete) */
  destructive?: boolean
}

const Dialog: React.FC<DialogProps> = ({
  name,
  title,
  className,
  maxWidth,
  children,
  staticBackdrop,
  keyboard,
  okBtn,
  cancelBtn,
  onClose,
  onOpen,
  destructive,
}: DialogProps) => {
  const [activeDialogs, closeDialog, getDepth] = useDialogStore(
    (s) => [s.activeDialogs, s.closeDialog, s.getDepth],
    shallow
  )

  const active = activeDialogs.includes(name)

  const close = useCallback(() => {
    onClose && onClose()
    closeDialog(name)
  }, [name, onClose])

  // Close dialog on "ESC"
  useEffect(() => {
    if (!keyboard || !active) return

    const closeOnEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', closeOnEsc)
    return () => document.removeEventListener('keydown', closeOnEsc)
  }, [keyboard, close, name, active])

  // Call onOpen when dialog becomes active
  useEffect(() => {
    if (active && typeof onOpen === 'function') onOpen()
  }, [active, onOpen])

  if (!active) return null
  return (
    <div
      onClick={() => !staticBackdrop && close()}
      style={{ zIndex: getDepth(name) }}
      className="fixed inset-0 p-2 overflow-y-auto text-black bg-black dark:text-gray bg-opacity-30"
      id={name}
      data-testid={name}>
      <div
        onClick={(e) => {
          e.stopPropagation()
        }}
        style={{ maxWidth }}
        className={classNames(
          'overflow-y-auto bg-white dark:bg-black-bg2 rounded-md shadow-thick flex flex-col pt-2 pb-3 px-3 w-full min-w-[250px] mx-auto sm:my-4',
          className
        )}>
        <div className="flex flex-row items-center p-2">
          <span className="flex-1 text-lg font-semibold">{title}</span>
          <button type="button" className="ml-2" onClick={close}>
            <i className="text-xl nf-icon nf-close" />
          </button>
        </div>
        <div className="p-2">{children}</div>
        {(okBtn || cancelBtn) && (
          <DialogButtons
            dialogName={name}
            okBtn={okBtn}
            cancelBtn={cancelBtn}
            destructive={destructive}
          />
        )}
      </div>
    </div>
  )
}

Dialog.defaultProps = {
  maxWidth: 'fit-content',
  keyboard: true,
  staticBackdrop: false,
}

export default Dialog
